"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiAuthFetch } from "@/lib/authFetch";
import { Button } from "@/components/ui/button";
import { ClientLogger } from "@/lib/clientLogger";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useScopePolicy } from "@/components/custom/contexts/scopePolicyContext";
import { fetchCaseInfo } from "@/lib/threadDocumentOps";

/**
 * praetor-mcp scope-filter approval card (v7+ protocol). Renders one
 * `data-mcp-elicitation` SSE event. The card shows exactly two buttons —
 * "Allow full read access to case 'X'" and "Deny" — regardless of whether
 * the underlying out-of-scope tool was case-scoped, doc-scoped, or
 * lookup-result-scoped: every approval widens scope at case granularity.
 * Pre-v7 the form carried a 3- or 4-radio `decision` enum; that enum is
 * gone since praetor-mcp 7.0.0, so we no longer parse the schema.
 *
 * On accept the card mirrors the approval into `scopePolicyContext` BEFORE
 * the network call (a transient POST failure does not lose the user's
 * intent — the next turn re-sends the expanded policy). On 204 success the
 * card hides itself; on failure it stays mounted with an inline error
 * message and the server-side dispatcher times the elicit out.
 */

type RawContext = {
  ctxId?: string;
  kind?: string;
  caseId?: string | null;
  documentId?: string | null;
  toolName?: string;
};

type CardState = "idle" | "submitting" | "error";

export type ElicitationCardProps = {
  id: string;
  message: string;
  /** Schema is kept on the wire for forward-compat; v7 card ignores it. */
  schema: unknown;
  context: unknown;
};

function parseContext(ctx: unknown): RawContext {
  if (!ctx || typeof ctx !== "object") return {};
  return ctx as RawContext;
}

export function ElicitationCard({ id, message, context }: ElicitationCardProps) {
  const { t } = useTranslation();
  const { addApprovedCase } = useScopePolicy();
  const [state, setState] = useState<CardState>("idle");
  const [submitted, setSubmitted] = useState(false);
  const [expired, setExpired] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [resolvedCaseName, setResolvedCaseName] = useState<string | null>(null);
  const [resolvedCaseNumber, setResolvedCaseNumber] = useState<string | null>(null);

  const ctx = useMemo(() => parseContext(context), [context]);

  useEffect(() => {
    if (!ctx.caseId) return;
    let cancelled = false;
    fetchCaseInfo(ctx.caseId)
      .then(info => {
        if (cancelled || !info) return;
        if (info.name) setResolvedCaseName(info.name);
        if (info.caseNumber) setResolvedCaseNumber(info.caseNumber);
      })
      .catch(() => { /* fall back to raw caseId in label */ });
    return () => { cancelled = true; };
  }, [ctx.caseId]);

  const acceptLabel = useMemo(() => {
    // Prefer "Name (CaseNumber)" — never the raw GUID, which is meaningless to a
    // lawyer. Degrade gracefully to name-only / number-only, and only fall back
    // to the opaque caseId if neither resolved.
    let display: string;
    if (resolvedCaseName && resolvedCaseNumber) {
      display = `${resolvedCaseName} (${resolvedCaseNumber})`;
    } else if (resolvedCaseName) {
      display = resolvedCaseName;
    } else if (resolvedCaseNumber) {
      display = resolvedCaseNumber;
    } else {
      display = ctx.caseId ?? "";
    }
    return t("elicitation.acceptAllowCase", { name: display });
  }, [resolvedCaseName, resolvedCaseNumber, ctx.caseId, t]);

  const respond = useCallback(async (action: "accept" | "decline") => {
    if (state === "submitting") return;

    // Mirror accept into scopePolicyContext BEFORE the network call. v7: every
    // accept promotes the resolved case to wildcard for the rest of this chat.
    if (action === "accept" && ctx.caseId) {
      addApprovedCase(ctx.caseId);
    }

    setState("submitting");
    setErrorMessage(null);

    try {
      const res = await apiAuthFetch(`api/chat/elicitation/${encodeURIComponent(id)}/respond`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action }),
      });

      if (res.status === 404) {
        // Elicitation expired or server restarted — entry no longer in memory.
        ClientLogger.info("Elicitation expired (404)", {
          component: "ElicitationCard",
          metadata: { elicitId: id, action },
        });
        setExpired(true);
        return;
      }

      if (!res.ok) {
        ClientLogger.warn("Elicitation respond failed", {
          component: "ElicitationCard",
          metadata: { elicitId: id, status: res.status, action },
        });
        setState("error");
        setErrorMessage(t("elicitation.error.network"));
        return;
      }

      setSubmitted(true);
    } catch (err) {
      ClientLogger.warn("Elicitation respond threw", {
        component: "ElicitationCard",
        error: err instanceof Error ? err : undefined,
        metadata: { elicitId: id, action },
      });
      setState("error");
      setErrorMessage(t("elicitation.error.network"));
    }
  }, [addApprovedCase, ctx.caseId, id, state, t]);

  if (submitted) return null;

  if (expired) {
    return (
      <div className="aui-elicitation-card my-3 flex w-full items-center gap-2 rounded-lg border border-muted bg-muted/30 px-4 py-2 text-xs text-muted-foreground">
        <span>{t("elicitation.expired")}</span>
      </div>
    );
  }

  return (
    <div className="aui-elicitation-card my-3 flex w-full flex-col gap-3 rounded-lg border border-amber-300/60 bg-amber-50/40 p-4 text-sm">
      <div className="aui-elicitation-card-message text-foreground">
        <Markdown remarkPlugins={[remarkGfm]}>{message}</Markdown>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={state === "submitting"}
          onClick={() => respond("accept")}
          className="border-emerald-300 text-emerald-700 hover:bg-emerald-600 hover:text-white hover:border-emerald-600"
        >
          {acceptLabel}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={state === "submitting"}
          onClick={() => respond("decline")}
        >
          {t("elicitation.deny")}
        </Button>
      </div>

      {state === "error" && errorMessage && (
        <p className="text-xs text-destructive">{errorMessage}</p>
      )}
    </div>
  );
}
